import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send } from 'lucide-react';

const ContatoPage = () => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    empresa: '',
    mensagem: '',
    servico: 'geral'
  });

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulação de envio de formulário
    setFormStatus({
      submitted: true,
      success: true,
      message: 'Mensagem enviada com sucesso! Entraremos em contato em breve.'
    });
    
    // Limpar formulário após envio
    setFormData({
      nome: '',
      email: '',
      telefone: '',
      empresa: '',
      mensagem: '',
      servico: 'geral'
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1A3A5F] to-[#4D8BC9] text-white">
        <div className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Contato</h1>
            <p className="text-xl mb-8">
              Entre em contato conosco e descubra como podemos ajudar sua empresa
            </p>
          </motion.div>
        </div>
      </section>

      {/* Informações de Contato e Formulário */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Informações de Contato */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:w-1/3"
            >
              <h2 className="text-3xl font-bold mb-6 text-[#1A3A5F]">Informações de Contato</h2>
              <p className="text-lg text-gray-700 mb-8">
                Entre em contato conosco através dos canais abaixo ou preencha o formulário ao lado. 
                Teremos prazer em atendê-lo e responder a todas as suas dúvidas.
              </p>

              <div className="space-y-6">
                <div className="flex items-start">
                  <MapPin className="text-[#4D8BC9] mt-1 mr-4" size={24} />
                  <div>
                    <h3 className="font-bold text-[#1A3A5F] mb-1">Endereço</h3>
                    <p className="text-gray-700">
                      Av. Paulista, 1000<br />
                      São Paulo - SP<br />
                      CEP: 01310-100<br />
                      Brasil
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Phone className="text-[#4D8BC9] mt-1 mr-4" size={24} />
                  <div>
                    <h3 className="font-bold text-[#1A3A5F] mb-1">Telefone</h3>
                    <p className="text-gray-700">
                      (11) 3456-7890<br />
                      (11) 98765-4321
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Mail className="text-[#4D8BC9] mt-1 mr-4" size={24} />
                  <div>
                    <h3 className="font-bold text-[#1A3A5F] mb-1">E-mail</h3>
                    <p className="text-gray-700">
                      contato@m7corp.com.br<br />
                      comercial@m7corp.com.br
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10">
                <h3 className="font-bold text-[#1A3A5F] mb-4">Horário de Atendimento</h3>
                <p className="text-gray-700">
                  Segunda a Sexta: 8h às 18h<br />
                  Sábado: 9h às 13h
                </p>
              </div>
            </motion.div>

            {/* Formulário de Contato */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:w-2/3"
            >
              <div className="bg-white p-8 rounded-lg shadow-md">
                <h2 className="text-3xl font-bold mb-6 text-[#1A3A5F]">Envie uma Mensagem</h2>
                
                {formStatus.submitted && (
                  <div className={`p-4 mb-6 rounded-md ${formStatus.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {formStatus.message}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="nome" className="block text-gray-700 font-medium mb-2">Nome Completo *</label>
                      <input
                        type="text"
                        id="nome"
                        name="nome"
                        value={formData.nome}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4D8BC9] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-gray-700 font-medium mb-2">E-mail *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4D8BC9] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="telefone" className="block text-gray-700 font-medium mb-2">Telefone *</label>
                      <input
                        type="tel"
                        id="telefone"
                        name="telefone"
                        value={formData.telefone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4D8BC9] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="empresa" className="block text-gray-700 font-medium mb-2">Empresa</label>
                      <input
                        type="text"
                        id="empresa"
                        name="empresa"
                        value={formData.empresa}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4D8BC9] focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="servico" className="block text-gray-700 font-medium mb-2">Serviço de Interesse</label>
                    <select
                      id="servico"
                      name="servico"
                      value={formData.servico}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4D8BC9] focus:border-transparent"
                    >
                      <option value="geral">Informações Gerais</option>
                      <option value="inspecao">Inspeção de Equipamentos</option>
                      <option value="integridade">Integridade de Equipamentos</option>
                      <option value="ensaios">Ensaios Não Destrutivos</option>
                      <option value="normativas">Adequação a Normativas</option>
                      <option value="calculo">Cálculo e Dimensionamento</option>
                      <option value="engenharia">Projetos de Engenharia</option>
                      <option value="treinamento">Treinamento e Capacitação</option>
                    </select>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="mensagem" className="block text-gray-700 font-medium mb-2">Mensagem *</label>
                    <textarea
                      id="mensagem"
                      name="mensagem"
                      value={formData.mensagem}
                      onChange={handleChange}
                      required
                      rows="5"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4D8BC9] focus:border-transparent"
                    ></textarea>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      className="bg-[#1A3A5F] hover:bg-[#0F2A4F] text-white font-bold px-8 py-3 rounded-md transition-colors flex items-center"
                    >
                      Enviar Mensagem
                      <Send size={18} className="ml-2" />
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mapa */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4 text-[#1A3A5F]">Nossa Localização</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Estamos localizados em uma região de fácil acesso, no coração da cidade de São Paulo.
            </p>
          </motion.div>

          <div className="bg-white p-4 rounded-lg shadow-md">
            <div className="h-96 bg-gray-200 rounded-md flex items-center justify-center">
              <div className="text-center">
                <MapPin className="h-12 w-12 text-[#4D8BC9] mx-auto mb-4" />
                <p className="text-gray-700">Mapa indisponível no momento</p>
                <p className="text-gray-500 text-sm">Av. Paulista, 1000 - São Paulo, SP</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Escritórios */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4 text-[#1A3A5F]">Nossos Escritórios</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Estamos presentes em diversas regiões do Brasil para melhor atender nossos clientes.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Escritório 1 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-lg shadow-md"
            >
              <h3 className="text-xl font-bold mb-2 text-[#1A3A5F]">São Paulo (Matriz)</h3>
              <p className="text-gray-700 mb-4">
                Av. Paulista, 1000<br />
                São Paulo - SP<br />
                CEP: 01310-100<br />
                Tel: (11) 3456-7890
              </p>
            </motion.div>

            {/* Escritório 2 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-lg shadow-md"
            >
              <h3 className="text-xl font-bold mb-2 text-[#1A3A5F]">Rio de Janeiro</h3>
              <p className="text-gray-700 mb-4">
                Av. Rio Branco, 500<br />
                Rio de Janeiro - RJ<br />
                CEP: 20040-002<br />
                Tel: (21) 3456-7890
              </p>
            </motion.div>

            {/* Escritório 3 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-lg shadow-md"
            >
              <h3 className="text-xl font-bold mb-2 text-[#1A3A5F]">Belo Horizonte</h3>
              <p className="text-gray-700 mb-4">
                Av. Afonso Pena, 300<br />
                Belo Horizonte - MG<br />
                CEP: 30130-001<br />
                Tel: (31) 3456-7890
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1A3A5F] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Pronto para transformar seus desafios em soluções?</h2>
            <p className="text-xl mb-8">
              Entre em contato conosco e descubra como podemos ajudar sua empresa a garantir segurança, 
              conformidade e eficiência operacional.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a 
                href="tel:+551134567890" 
                className="bg-white text-[#1A3A5F] hover:bg-gray-100 font-bold px-8 py-3 rounded-md transition-colors flex items-center justify-center"
              >
                <Phone size={18} className="mr-2" />
                (11) 3456-7890
              </a>
              <a 
                href="mailto:contato@m7corp.com.br" 
                className="bg-transparent border-2 border-white hover:bg-white hover:text-[#1A3A5F] text-white font-bold px-8 py-3 rounded-md transition-colors flex items-center justify-center"
              >
                <Mail size={18} className="mr-2" />
                contato@m7corp.com.br
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContatoPage;
